import React, { useMemo, useState } from 'react';
import { Alert, Button } from 'react-bootstrap';
import { Plus } from 'lucide-react';
import { UseParkingQueueReturn } from '../../hooks/parkingQueue';
import { useIsAdmin } from '../../hooks/useIsAdmin';
import ParkingAreaCard from './ParkingAreaCard';
import CreateParkingAreaFlow from './CreateParkingAreaFlow';
import './Parking.css';

/**
 * The Områden tab of /parking: every building's parking areas as drawing
 * cards in a horizontal row per building (areas have no inherent order), and
 * buildings without areas omitted.
 */
const ParkingAreasOverview: React.FC<{ queue: UseParkingQueueReturn }> = ({ queue }) => {
    const isAdmin = useIsAdmin();
    const [creating, setCreating] = useState(false);
    const { areasByBuilding, structureByBuilding, buildingName, error } = queue;
    // Every building the hook loaded (with or without areas), so a first area can be created anywhere.
    const buildingChoices = useMemo(() =>
        Object.keys(areasByBuilding).map(key => ({ key, name: buildingName(key) })).sort((a, b) => a.name.localeCompare(b.name, 'sv')),
        [areasByBuilding, buildingName]);

    const buildings = useMemo(() =>
        Object.entries(areasByBuilding)
            .map(([buildingKey, areas]) => ({ buildingKey, name: buildingName(buildingKey), areas: Object.values(areas) }))
            .filter(b => b.areas.length > 0)
            .sort((a, b) => a.name.localeCompare(b.name, 'sv')),
        [areasByBuilding, buildingName]);

    return (
        <>
            {error && <Alert variant="danger">{error}</Alert>}
            <div className="parking-tab-content std-box">
                {isAdmin && (
                    <div className="parking-tab-content-header">
                        <span className="text-muted small">{buildings.length} {buildings.length === 1 ? 'byggnad' : 'byggnader'} med parkering</span>
                        <Button variant="primary" className="ms-auto" onClick={() => setCreating(true)}>
                            <Plus size={16} className="me-1" />Nytt parkeringsområde
                        </Button>
                    </div>
                )}
                {buildings.length === 0 ? (
                    <p className="parking-empty">Ingen parkering registrerad i någon byggnad.</p>
                ) : buildings.map(b => (
                    <section key={b.buildingKey} className="parking-building-section">
                        <h2 className="parking-section-title">{b.name}</h2>
                        <div className="parking-area-cards">
                            {b.areas.map(area => (
                                <ParkingAreaCard
                                    key={area.key}
                                    area={area}
                                    structure={structureByBuilding[b.buildingKey]}
                                    to={`/building/${b.buildingKey}/parking/${area.key}`}
                                />
                            ))}
                        </div>
                    </section>
                ))}
            </div>
            {creating && <CreateParkingAreaFlow buildings={buildingChoices} onHide={() => setCreating(false)} onCreated={() => setCreating(false)} />}
        </>
    );
};

export default ParkingAreasOverview;
